import { useEffect, useState } from 'react';
import {
  Container,
  Stack,
  Title,
  Group,
  Alert,
  Text,
  Badge,
  Button,
  ActionIcon,
  Tooltip,
  ScrollArea,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconUser,
  IconUserCheck,
  IconUserX,
  IconCopy,
  IconGauge,
  IconBook,
  IconStack2,
  IconFlame,
  IconAdjustments,
  IconDatabase,
  IconDownload,
  IconRocket,
  IconTicket,
  IconMail,
  IconSettings,
} from '@tabler/icons-react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useAppUser } from '../../hooks/useAppUser';
import { LoadingScreen } from '../../components/Layout/LoadingScreen';

export type ImpersonationStatus = {
  isImpersonating: boolean;
  impersonatingUserId: string | null;
};

export type AdminOutletContext = {
  impersonationStatus: ImpersonationStatus | null;
};

// Typed accessor for admin pages rendered inside the layout's <Outlet />.
export const useAdminContext = () => useOutletContext<AdminOutletContext>();

const NAV_ITEMS: {
  path: string;
  label: string;
  icon: typeof IconGauge;
  // Overview must only highlight on an exact match, or it would claim every
  // /admin/* route.
  exact?: boolean;
}[] = [
  { path: '/admin', label: 'Overview', icon: IconGauge, exact: true },
  { path: '/admin/users', label: 'Users', icon: IconUser },
  { path: '/admin/catalog', label: 'Catalog', icon: IconBook },
  { path: '/admin/seed-queue', label: 'Seed Queue', icon: IconStack2 },
  { path: '/admin/trending', label: 'Trending', icon: IconFlame },
  { path: '/admin/taste-axes', label: 'Taste Axes', icon: IconAdjustments },
  { path: '/admin/ingredients', label: 'Ingredients', icon: IconDatabase },
  { path: '/admin/imports', label: 'Imports', icon: IconDownload },
  { path: '/admin/releases', label: 'Releases', icon: IconRocket },
  { path: '/admin/vouchers', label: 'Vouchers', icon: IconTicket },
  { path: '/admin/emails', label: 'Emails', icon: IconMail },
  { path: '/admin/system', label: 'System', icon: IconSettings },
];

/**
 * Shared frame for every /admin route: the admin guard, the section nav, and
 * the impersonation banner (which should be visible whichever admin page
 * you're on). Pages render below via <Outlet /> and bring their own
 * Container, so pre-existing pages slot in unchanged.
 */
export const AdminLayout = () => {
  const { user, loading: userLoading } = useAppUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [impersonationStatus, setImpersonationStatus] =
    useState<ImpersonationStatus | null>(null);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);

  useEffect(() => {
    if (!user?.isAdmin) return;

    const checkImpersonationStatus = async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/current-impersonation`,
          {
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include',
          }
        );
        if (response.ok) {
          setImpersonationStatus(await response.json());
        }
      } catch (error) {
        console.error('Failed to check impersonation status:', error);
      }
    };

    checkImpersonationStatus();
  }, [user]);

  const handleStopImpersonation = async () => {
    if (!user) return;

    setStoppingImpersonation(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/stop-impersonation`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        }
      );

      if (response.ok) {
        // Small delay to ensure cookie is cleared before reload
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Error',
          message: error.error || 'Failed to stop impersonation',
          color: 'red',
        });
        setStoppingImpersonation(false);
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to stop impersonation',
        color: 'red',
      });
      setStoppingImpersonation(false);
    }
  };

  const copyUserId = (uid: string) => {
    navigator.clipboard.writeText(uid);
    notifications.show({
      message: 'User ID copied',
      color: 'green',
      autoClose: 2000,
    });
  };

  if (userLoading) {
    return <LoadingScreen />;
  }

  if (!user?.isAdmin) {
    return (
      <Container size="md" py="xl">
        <Alert
          icon={<IconAlertCircle size="1rem" />}
          title="Access Denied"
          color="red"
          radius="lg"
        >
          You do not have admin privileges to access this page.
        </Alert>
      </Container>
    );
  }

  const impersonatedUid = impersonationStatus?.impersonatingUserId ?? '';

  return (
    <>
      <Container size="xl" pt="xl">
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="nowrap">
            <div>
              <Title order={2}>Admin</Title>
              <Text c="dimmed" size="sm">
                {user.email}
              </Text>
            </div>
            <Badge color="blue" leftSection={<IconUser size="0.8rem" />}>
              Admin
            </Badge>
          </Group>

          <ScrollArea type="never">
            <Group gap="xs" wrap="nowrap">
              {NAV_ITEMS.map(({ path, label, icon: Icon, exact }) => {
                const active = exact
                  ? location.pathname === path
                  : location.pathname === path ||
                    location.pathname.startsWith(`${path}/`);
                return (
                  <Button
                    key={path}
                    size="xs"
                    variant={active ? 'filled' : 'light'}
                    leftSection={<Icon size="0.9rem" />}
                    onClick={() => navigate(path)}
                    style={{ flexShrink: 0 }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Group>
          </ScrollArea>

          {impersonationStatus?.isImpersonating && (
            <Alert
              icon={<IconUserCheck size="1rem" />}
              title="Currently Impersonating"
              color="orange"
              radius="lg"
            >
              <Group justify="space-between" align="center">
                <div>
                  <Group gap="xs" align="center" wrap="nowrap">
                    <Text>User ID: {impersonatedUid}</Text>
                    <Tooltip label="Copy user ID">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        onClick={() => copyUserId(impersonatedUid)}
                      >
                        <IconCopy size="0.8rem" />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Text size="sm">You are currently acting as this user</Text>
                </div>
                <Button
                  leftSection={<IconUserX size="1rem" />}
                  color="red"
                  variant="outline"
                  onClick={handleStopImpersonation}
                  loading={stoppingImpersonation}
                  disabled={stoppingImpersonation}
                >
                  Stop Impersonation
                </Button>
              </Group>
            </Alert>
          )}
        </Stack>
      </Container>
      <Outlet context={{ impersonationStatus } satisfies AdminOutletContext} />
    </>
  );
};
